import React from "react"
import styled from "styled-components"

import defaultTokens from "../../styles/LibUIThemeTokens"

/**
 * Component wrapper that can be used as a Theme provider, it sets css variables
 * for the components imported from @autouncle/ui
 */

const LibUIThemeProviderDiv = styled.div<{
  tokens: typeof defaultTokens
}>`
  /* Don't act like a box, so the flow/encapsulation isn't disrupted */
  display: contents;

  /* Functional tokens */

  /* Brand */
  --brand-color: ${({ tokens }) => tokens("colors.secondary.600")};
  /* --brand-inverse-color: theme("colors.blue.dark.500"); */

  /* Background */
  --background-default: ${({ tokens }) => tokens("colors.white")};
  /* --background-neutral-subtle: theme("colors.grey.100"); */
  /* --background-neutral-contrast: theme("colors.grey.600"); */
  /* --background-standout-subtle: theme("colors.blue.dark.100"); */
  /* --background-standout-contrast: theme("colors.blue.dark.500"); */
  /* --background-highlight-subtle: theme("colors.blue.dark.100"); */
  /* --background-highlight-contrast: theme("colors.blue.dark.700"); */
  /* --background-positive-subtle: theme("colors.green.200"); */
  /* --background-positive-contrast: theme("colors.green.700"); */
  /* --background-negative-subtle: theme("colors.red.100"); */
  /* --background-negative-contrast: theme("colors.red.600"); */
  /* --background-information-subtle: theme("colors.yellow.200"); */
  /* --background-information-contrast: theme("colors.yellow.600"); */

  /* Text color */
  --text-color-default: ${({ tokens }) => tokens("colors.cool.800")};
  /* --text-color-placeholder: theme("colors.grey.600"); */
  /* --text-color-body: theme("colors.grey.600"); */
  /* --text-color-disabled: theme("colors.grey.400"); */
  /* --text-color-link: theme("colors.blue.dark.500"); */
  /* --text-color-positive: theme("colors.green.800"); */
  /* --text-color-negative: theme("colors.red.700"); */
  /* --text-color-information: theme("colors.yellow.700"); */
  --text-color-inverse: ${({ tokens }) => tokens("colors.white")};

  /* Fonts - weight */
  /* --text-weight-regular: theme("fontWeight.regular"); */
  /* --text-weight-medium: theme("fontWeight.medium"); */
  /* --text-weight-bold: theme("fontWeight.bold"); */
  --text-weight-semibold: ${({ tokens }) => tokens("fontWeight.semibold")};

  /* Fonts - family */
  --text-font-family-primary: ${({ tokens }) => tokens("fontFamily.body")};
  --text-font-family-secondary: ${({ tokens }) => tokens("fontFamily.title")};

  /* Fonts - text-display-1 */
  --text-display-1-font-size: ${({ tokens }) => tokens("fontSize.2xl")};
  --text-display-1-line-height: ${({ tokens }) => tokens("lineHeight.2xl")};

  @media (min-width: ${({ tokens }) => tokens("screens.md")}) {
    --text-display-1-font-size: ${({ tokens }) => tokens("fontSize.3xl")};
    --text-display-1-line-height: ${({ tokens }) => tokens("lineHeight.3xl")};
  }

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --text-display-1-font-size: ${({ tokens }) => tokens("fontSize.4xl")};
    --text-display-1-line-height: ${({ tokens }) => tokens("lineHeight.4xl")};
  }

  /* Fonts - text-display-2 */
  --text-display-2-font-size: ${({ tokens }) => tokens("fontSize.xl")};
  --text-display-2-line-height: ${({ tokens }) => tokens("lineHeight.xl")};

  @media (min-width: ${({ tokens }) => tokens("screens.md")}) {
    --text-display-2-font-size: ${({ tokens }) => tokens("fontSize.2xl")};
    --text-display-2-line-height: ${({ tokens }) => tokens("lineHeight.2xl")};
  }

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --text-display-2-font-size: ${({ tokens }) => tokens("fontSize.3xl")};
    --text-display-2-line-height: ${({ tokens }) => tokens("lineHeight.3xl")};
  }

  /* Fonts - text-heading-1 */
  --text-heading-1-font-size: ${({ tokens }) => tokens("fontSize.lg")};
  --text-heading-1-line-height: ${({ tokens }) => tokens("lineHeight.lg")};

  @media (min-width: ${({ tokens }) => tokens("screens.md")}) {
    --text-heading-1-font-size: ${({ tokens }) => tokens("fontSize.xl")};
    --text-heading-1-line-height: ${({ tokens }) => tokens("lineHeight.xl")};
  }

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --text-heading-1-font-size: ${({ tokens }) => tokens("fontSize.xl")};
    --text-heading-1-line-height: ${({ tokens }) => tokens("lineHeight.xl")};
  }

  /* Fonts - text-heading-2 */
  --text-heading-2-font-size: ${({ tokens }) => tokens("fontSize.base")};
  --text-heading-2-line-height: ${({ tokens }) => tokens("lineHeight.base")};

  @media (min-width: ${({ tokens }) => tokens("screens.md")}) {
    --text-heading-2-font-size: ${({ tokens }) => tokens("fontSize.lg")};
    --text-heading-2-line-height: ${({ tokens }) => tokens("lineHeight.lg")};
  }

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --text-heading-2-font-size: ${({ tokens }) => tokens("fontSize.lg")};
    --text-heading-2-line-height: ${({ tokens }) => tokens("lineHeight.lg")};
  }

  /* Fonts - text-heading-3 */
  --text-heading-3-font-size: ${({ tokens }) => tokens("fontSize.sm")};
  --text-heading-3-line-height: ${({ tokens }) => tokens("lineHeight.sm")};

  @media (min-width: ${({ tokens }) => tokens("screens.md")}) {
    --text-heading-3-font-size: ${({ tokens }) => tokens("fontSize.base")};
    --text-heading-3-line-height: ${({ tokens }) => tokens("lineHeight.base")};
  }

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --text-heading-3-font-size: ${({ tokens }) => tokens("fontSize.base")};
    --text-heading-3-line-height: ${({ tokens }) => tokens("lineHeight.base")};
  }

  /* Fonts - text-body-base */
  --text-body-base-font-size: ${({ tokens }) => tokens("fontSize.base")};
  --text-body-base-line-height: ${({ tokens }) => tokens("lineHeight.base")};

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --text-body-base-font-size: ${({ tokens }) => tokens("fontSize.sm")};
    --text-body-base-line-height: ${({ tokens }) => tokens("lineHeight.sm")};
  }

  /* Fonts - text-body-small */
  --text-body-small-font-size: ${({ tokens }) => tokens("fontSize.sm")};
  --text-body-small-line-height: ${({ tokens }) => tokens("lineHeight.sm")};

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --text-body-small-font-size: ${({ tokens }) => tokens("fontSize.xs")};
    --text-body-small-line-height: ${({ tokens }) => tokens("lineHeight.xs")};
  }

  /* Fonts - text-body-xsmall */
  --text-body-xsmall-font-size: ${({ tokens }) => tokens("fontSize.xs")};
  --text-body-xsmall-line-height: ${({ tokens }) => tokens("lineHeight.xs")};

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --text-body-xsmall-font-size: ${({ tokens }) => tokens("fontSize.2xs")};
    --text-body-xsmall-line-height: ${({ tokens }) => tokens("lineHeight.2xs")};
  }

  /* Fonts - text-body-2xsmall */
  --text-body-2xsmall-font-size: ${({ tokens }) => tokens("fontSize.2xs")};
  --text-body-2xsmall-line-height: ${({ tokens }) => tokens("lineHeight.2xs")};

  /* Fonts - text-caption */
  --text-caption-font-size: ${({ tokens }) => tokens("fontSize.sm")};
  --text-caption-line-height: ${({ tokens }) => tokens("lineHeight.sm")};

  /* Fonts - text-subtitle */
  --text-subtitle-font-size: ${({ tokens }) => tokens("fontSize.xs")};
  --text-subtitle-line-height: ${({ tokens }) => tokens("lineHeight.xs")};

  /* Fonts - text-button-font-size */
  --text-button-font-size-base: ${({ tokens }) => tokens("fontSize.sm")};
  --text-button-font-size-large: ${({ tokens }) => tokens("fontSize.base")};

  /* Fonts - text-button-line-height */
  --text-button-line-height-base: ${({ tokens }) => tokens("lineHeight.sm")};
  --text-button-line-height-large: ${({ tokens }) => tokens("lineHeight.base")};

  /* Fonts - text-input */

  /* Overrides the text field tokens */
  /* Higher font size for mobile to prevent zooming on field focus */
  --text-input-font-size: ${({ tokens }) => tokens("fontSize.base")};
  --text-input-line-height: ${({ tokens }) => tokens("lineHeight.base")};

  @media (min-width: ${({ tokens }) => tokens("screens.md")}) {
    --text-input-font-size: ${({ tokens }) => tokens("fontSize.sm")};
    --text-input-line-height: ${({ tokens }) => tokens("lineHeight.sm")};
  }

  /* Spacing - inside components values */
  --spacing-3xs: ${({ tokens }) => tokens("spacing.3xs")};
  --spacing-2xs: ${({ tokens }) => tokens("spacing.2xs")};
  --spacing-xs: ${({ tokens }) => tokens("spacing.xs")};
  --spacing-sm: ${({ tokens }) => tokens("spacing.sm")};
  --spacing-md: ${({ tokens }) => tokens("spacing.md")};
  --spacing-2md: ${({ tokens }) => tokens("spacing.2md")};

  /* Spacing - layout related values - they include breakpoints as they grow with the screen */
  --spacing-lg: ${({ tokens }) => tokens("spacing.lg")};
  --spacing-xl: ${({ tokens }) => tokens("spacing.xl")};
  --spacing-2xl: ${({ tokens }) => tokens("spacing.2xl")};

  @media (min-width: ${({ tokens }) => tokens("screens.xl")}) {
    --spacing-lg: ${({ tokens }) => tokens("spacing.xl")};
  }

  --spacing-xl: ${({ tokens }) => tokens("spacing.xl")};

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --spacing-xl: ${({ tokens }) => tokens("spacing.2xl")};
  }

  @media (min-width: ${({ tokens }) => tokens("screens.xl")}) {
    --spacing-xl: ${({ tokens }) => tokens("spacing.11")};
  }

  --spacing-2xl: ${({ tokens }) => tokens("spacing.2xl")};

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --spacing-2xl: ${({ tokens }) => tokens("spacing.11")};
  }

  @media (min-width: ${({ tokens }) => tokens("screens.xl")}) {
    --spacing-2xl: ${({ tokens }) => tokens("spacing.12")};
  }

  /* Grid */
  --grid-gap-xs: ${({ tokens }) => tokens("spacing.xs")};
  --grid-gap-sm: ${({ tokens }) => tokens("spacing.sm")};
  --grid-gap: ${({ tokens }) => tokens("spacing.md")};
  --grid-gap-lg: ${({ tokens }) => tokens("spacing.2md")};

  @media (min-width: ${({ tokens }) => tokens("screens.sm")}) {
    --grid-gap-xs: ${({ tokens }) => tokens("spacing.sm")};
    --grid-gap-sm: ${({ tokens }) => tokens("spacing.md")};
    --grid-gap: ${({ tokens }) => tokens("spacing.2md")};
    --grid-gap-lg: ${({ tokens }) => tokens("spacing.lg")};
  }

  /* Tappable sizes */

  /* Minimum tappable size must be at least 40px on mobile */
  /* --minimum-tappable-size: 40px; */
  --tappable-size-sm: ${({ tokens }) => tokens("spacing.2xl")};
  --tappable-size: ${({ tokens }) => tokens("spacing.2xl")};
  --tappable-size-lg: ${({ tokens }) => tokens("spacing.10")};

  @media (min-width: ${({ tokens }) => tokens("screens.md")}) {
    /* For larger screens tappable size is less of an issue */
    --tappable-size-sm: ${({ tokens }) => tokens("spacing.xl")};
  }

  /* Disabled states */
  --disabled-opacity: 0.6;

  /* Animation */
  /* --transition-default: theme("transitionDuration.400"); */
  /* --transition-fast: theme("transitionDuration.200"); */
  /* --transition-slow: theme("transitionDuration.800"); */

  /* Icons */
  --icon-width-sm: ${({ tokens }) => tokens("spacing.md")};
  --icon-width-md: ${({ tokens }) => tokens("spacing.2md")};
  --icon-width-lg: ${({ tokens }) => tokens("spacing.lg")};

  /* Lines - width */
  --line-width-default: ${({ tokens }) => tokens("lineWidth.default")};

  /* Lines - color */
  /* --line-color-adaptive: rgb(0 0 0 / 20%); */
  --line-color-neutral: ${({ tokens }) => tokens("colors.cool.300")};
  /* --line-color-standout: theme("colors.blue.dark.300"); */
  /* --line-color-positive: theme("colors.green.500"); */
  /* --line-color-negative: theme("colors.red.200"); */
  /* --line-color-information: theme("colors.yellow.600"); */
  /* --line-color-highlight: theme("colors.blue.dark.700"); */

  /* Border radius */
  --border-radius-default: ${({ tokens }) => tokens("borderRadius.md")};
  --border-radius-sharp: ${({ tokens }) => tokens("borderRadius.sm")};
  --border-radius-smooth: ${({ tokens }) => tokens("borderRadius.lg")};

  /* Outline */
  /* --focus-outline-color: theme("colors.blue.dark.200"); */
  --focus-outline-width: ${({ tokens }) => tokens("spacing.3xs")};

  /* z-Index
    Using high values for backward compatibility
  */
  /* --z-index-sticky: 90; /* navbars, sticky headers, etc */
  /* --z-index-sidebar: 100; */
  /* --z-index-backdrop: 900; */
  /* --z-index-modals: 990; */
  /* --z-index-tooltips: 9990; */

  /* Backdrop */
  /* --backdrop-background-color: theme("colors.blue.dark.900"); */
  /* --backdrop-opacity: 0.8; */

  /* Button */
  /* --button-border-width: var(--line-width-default); */

  /* Button - Primary */
  /* --button-primary-background-color: theme("colors.blue.dark.500"); */
  /* --button-primary-hover-background-color: theme("colors.blue.dark.400"); */
  /* --button-primary-active-background-color: theme("colors.blue.dark.600"); */
  /* --button-primary-border-color: theme("colors.blue.dark.500"); */
  /* --button-primary-active-border-color: theme("colors.blue.dark.600"); */
  /* --button-primary-hover-border-color: theme("colors.blue.dark.400"); */
  /* --button-primary-color: theme("colors.white"); */
  /* --button-primary-hover-color: theme("colors.white"); */

  /* Button - Secondary */
  /* --button-secondary-background-color: theme("colors.white"); */
  /* --button-secondary-hover-background-color: theme("colors.white"); */
  /* --button-secondary-active-background-color: theme("colors.blue.dark.100"); */
  /* --button-secondary-border-color: theme("colors.grey.400"); */
  /* --button-secondary-active-border-color: theme("colors.blue.dark.500"); */
  /* --button-secondary-hover-border-color: theme("colors.blue.dark.500"); */
  /* --button-secondary-color: theme("colors.blue.dark.800"); */
  /* --button-secondary-hover-color: theme("colors.blue.dark.500"); */

  /* Button - Tertiary */
  /* --button-tertiary-background-color: theme("colors.grey.100"); */
  /* --button-tertiary-hover-background-color: theme("colors.grey.100"); */
  /* --button-tertiary-active-background-color: theme("colors.grey.200"); */
  /* --button-tertiary-border-color: theme("colors.grey.100"); */
  /* --button-tertiary-hover-border-color: theme("colors.grey.100"); */
  /* --button-tertiary-active-border-color: theme("colors.grey.200"); */
  /* --button-tertiary-color: theme("colors.blue.dark.500"); */
  /* --button-tertiary-hover-color: theme("colors.blue.dark.700"); */

  /* Button - Link */
  /* --button-link-background-color: transparent; */
  /* --button-link-hover-background-color: transparent; */
  /* --button-link-active-background-color: transparent; */
  /* --button-link-border-color: transparent; */
  /* --button-link-active-border-color: transparent; */
  /* --button-link-hover-border-color: transparent; */
  /* --button-link-color: theme("colors.blue.dark.500"); */
  /* --button-link-hover-color: theme("colors.blue.dark.700"); */

  /* Button - Positive */
  /* --button-positive-background-color: theme("colors.green.700"); */
  /* --button-positive-hover-background-color: theme("colors.green.600"); */
  /* --button-positive-active-background-color: theme("colors.green.800"); */
  /* --button-positive-border-color: theme("colors.green.700"); */
  /* --button-positive-active-border-color: theme("colors.green.800"); */
  /* --button-positive-hover-border-color: theme("colors.green.600"); */
  /* --button-positive-color: theme("colors.white"); */
  /* --button-positive-hover-color: theme("colors.white"); */

  /* Button - Negative */
  /* --button-negative-background-color: theme("colors.red.500"); */
  /* --button-negative-hover-background-color: theme("colors.red.400"); */
  /* --button-negative-active-background-color: theme("colors.red.600"); */
  /* --button-negative-border-color: theme("colors.red.500"); */
  /* --button-negative-active-border-color: theme("colors.red.600"); */
  /* --button-negative-hover-border-color: theme("colors.red.400"); */
  /* --button-negative-color: theme("colors.white"); */
  /* --button-negative-hover-color: theme("colors.white"); */

  /* ButtonIcon */
  /* --button-icon-hover-background-color: theme("colors.grey.100"); */
  /* --button-icon-active-background-color: theme("colors.grey.200"); */

  /* Fields - label */
  /* --field-label-color: theme("colors.grey.900"); */

  /* Fields - disabled */
  /* --field-disabled-border-color: theme("colors.grey.200"); */
  /* --field-disabled-color: theme("colors.grey.500"); */
  /* --field-disabled-background-color: theme("colors.grey.100"); */

  /* Fields - hint and error */
  /* --field-hint-color: theme("colors.grey.500"); */
  /* --field-error-color: theme("colors.red.500"); */

  /* Fields - Checkbox, and radio button */
  /* --field-box-color: theme("colors.grey.600"); */
  /* --field-box-color-checked: theme("colors.blue.dark.500"); */
  --field-box-height: ${({ tokens }) => tokens("spacing.lg")};

  /* Fields - Input, Select, and Textarea */
  --field-input-height-sm: ${({ tokens }) => tokens("spacing.xl")};
  --field-input-height-md: ${({ tokens }) =>
    tokens("spacing.2xl")}; /* Does not apply to textarea */
  --field-input-height-lg: ${({ tokens }) => tokens("spacing.10")};
  /* --field-input-color: theme("colors.grey.900"); */
  /* --field-input-background-color: var(--background-default); */
  /* --field-input-border-color: theme("colors.grey.400"); */
  /* --field-input-focus-border-color: theme("colors.blue.dark.400"); */

  /* Field icons */
  /* --field-icon-color: theme("colors.grey.600"); */
  /* --field-disabled-icon-color: theme("colors.grey.500"); */

  /* Components: Toggle */
  /* --toggle-height: var(--text-body-base-font-size); */
  /* --toggle-width: calc(var(--toggle-height) * 2); */
  /* --toggle-gutter: 2px; */
  /* --toggle-radius: calc(var(--toggle-height) / 2); */
  /* --toggle-dot-size: calc(var(--toggle-height) - (var(--toggle-gutter) * 2)); */

  /* Wrapper */
  /* --wrapper-only-large-screens-padding: 0; */
  --wrapper-only-small-screens-padding: ${({ tokens }) => tokens("spacing.md")};
  --wrapper-default-padding: ${({ tokens }) => tokens("spacing.md")};
  --wrapper-default-max-width: ${({ tokens }) => tokens("containerSizes.lg")};
  --wrapper-landing-max-width: ${({ tokens }) => tokens("containerSizes.lg")};
  /* The nested wrapper max-width is full width at smaller sizes and fixed at larger ones */
  --wrapper-nested-max-width: 100%;

  @media (min-width: ${({ tokens }) => tokens("screens.md")}) {
    --wrapper-default-padding: ${({ tokens }) => tokens("spacing.xl")};
    /* Limit nested wrapper on bigger screens */
    --wrapper-nested-max-width: ${({ tokens }) => tokens("containerSizes.md")};
  }

  @media (min-width: ${({ tokens }) => tokens("screens.lg")}) {
    --wrapper-only-large-screens-padding: ${({ tokens }) =>
      tokens("spacing.md")};
    --wrapper-only-small-screens-padding: 0;
  }

  /* Line charts */
  /* --line-chart-primary-color: theme("colors.blue.light.600"); */
  /* --line-chart-stroke-width: 3; */

  /* Box */
  --box-default-border: 1px solid var(--line-color-neutral);
  /* TODO: check! not present in new theme */
  /* --box-default-border-radius: ${({ tokens }) =>
    tokens("borderRadius.md")}; */
  --box-default-padding: ${({ tokens }) => tokens("spacing.lg")};
  --box-default-box-shadow: ${({ tokens }) => tokens("boxShadow.100")};
  /* --box-default-box-shadow-hover: theme("boxShadow.300"); */

  /* Ratings */
  /* --rating-0-color: theme("colors.rating.0"); */
  /* --rating-1-color: theme("colors.rating.1"); */
  /* --rating-2-color: theme("colors.rating.2"); */
  /* --rating-3-color: theme("colors.rating.3"); */
  /* --rating-4-color: theme("colors.rating.4"); */
  /* --rating-5-color: theme("colors.rating.5"); */

  /* Emission */
  /* --emission-color-2: theme("colors.emission.2"); */
  /* --emission-color-3: theme("colors.emission.3"); */
  /* --emission-color-4: theme("colors.emission.4"); */
  /* --emission-color-5: theme("colors.emission.5"); */

  /* Exterior color */
  /* --exterior-color-beige: theme("colors.exterior.beige"); */
  /* --exterior-color-black: theme("colors.exterior.black"); */
  /* --exterior-color-blue: theme("colors.exterior.blue"); */
  /* --exterior-color-brown: theme("colors.exterior.brown"); */
  /* --exterior-color-gold: theme("colors.exterior.gold"); */
  /* --exterior-color-green: theme("colors.exterior.green"); */
  /* --exterior-color-grey: theme("colors.exterior.grey"); */
  /* --exterior-color-orange: theme("colors.exterior.orange"); */
  /* --exterior-color-pink: theme("colors.exterior.pink"); */
  /* --exterior-color-purple: theme("colors.exterior.purple"); */
  /* --exterior-color-red: theme("colors.exterior.red"); */
  /* --exterior-color-silver: theme("colors.exterior.silver"); */
  /* --exterior-color-turquoise: theme("colors.exterior.turquoise"); */
  /* --exterior-color-white: theme("colors.exterior.white"); */
  /* --exterior-color-yellow: theme("colors.exterior.yellow"); */
  /* --exterior-color-border: theme("colors.grey.400"); */

  /* Aspect ratio */
  /* --aspect-ratio-widescreen: 16 / 9; */
  /* --aspect-ratio-square: 1 / 1; */
  /* --aspect-ratio-landscape: 4 / 3; */
  /* --aspect-ratio-portrait: 3 / 4; */

  /* Media sizes */
  --media-size-xs: ${({ tokens }) => tokens("spacing.13")};
  --media-size-sm: ${({ tokens }) => tokens("spacing.14")};

  /* Page */
  --page-aside-width: ${({ tokens }) => tokens("screens.sm")};

  /* Gallery */
  --image-background-filter: contrast(0.4) grayscale(0.2)
    blur(${({ tokens }) => tokens("spacing.3xs")}) brightness(1.4);

  /* Components: Spinner */
  /* --spinner-border-color: theme("colors.grey.400"); */
  /* --spinner-border-top-color: theme("colors.grey.700"); */
  --spinner-dash-width: ${({ tokens }) => tokens("spacing.3xs")};
  --spinner-size: ${({ tokens }) => tokens("spacing.2md")};
  --spinner-size-sm: ${({ tokens }) => tokens("spacing.sm")};
`

// Wrapper component that sets default token theme
const LibUIThemeProvider = ({
  tokens = defaultTokens,
  ...props
}: React.ComponentProps<typeof LibUIThemeProviderDiv> & {
  tokens?: typeof defaultTokens
}) => <LibUIThemeProviderDiv tokens={tokens} {...props} />

export default LibUIThemeProvider
